import { InputHTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';

import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import { FormInput } from '@/components/Input';
import UploadPhoto from '@/components/UploadPhoto';
import { PROFILE_IMAGE } from '@/constants/files';

import { PersonalInformationForm } from './schema';

const personalInfoFieldNames = [
  'first_name',
  'last_name',
  'middle_name',
  'job_title',
  'department',
  'email',
  'pronouns',
  'mobile_number',
  'note',
] as const;

type PersonalInfoField = {
  name: (typeof personalInfoFieldNames)[number];
  label: string;
} & InputHTMLAttributes<HTMLInputElement>;

export function PersonalInformation({
  disabled = false,
  disableUpload = false,
}: {
  disabled?: boolean;
  disableUpload?: boolean;
}) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PersonalInformationForm>();

  const photo = watch('photo');

  const nameFields: PersonalInfoField[] = [
    {
      name: 'first_name',
      label: 'First name',
      autoComplete: 'given-name',
    },
    {
      name: 'middle_name',
      label: 'Middle name',
      autoComplete: 'additional-name',
    },
    {
      name: 'last_name',
      label: 'Last name',
      autoComplete: 'family-name',
    },
  ];

  return (
    <InfoPanelContainer>
      <div className="text-base font-medium text-gray-900 capitalize pb-6 border-b border-gray-200">
        Personal information
      </div>
      <div className="space-y-4 mt-6">
        <UploadPhoto
          photo={photo || undefined}
          setPhoto={file => setValue('photo', file ?? null)}
          maxWidth={PROFILE_IMAGE.MAX_WIDTH}
          maxHeight={PROFILE_IMAGE.MAX_HEIGHT}
          disabled={disableUpload}
        />

        {/* name section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {nameFields.map(field => (
            <div key={field.name}>
              <FormInput
                error={!!errors[field.name]}
                disabled={disabled}
                {...field}
                {...register(field.name)}
              />
              {errors[field.name] && (
                <span className="text-sm text-red-400">
                  {errors[field.name]?.message}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Job title and pronouns section */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="col-span-2">
            <FormInput
              label="Job title/Position"
              autoComplete="organization-title"
              error={!!errors.job_title}
              disabled={disabled}
              {...register('job_title')}
            />
          </div>

          <div className="col-span-2">
            <FormInput
              label="Department"
              error={!!errors.department}
              disabled={disabled}
              {...register('department')}
            />
          </div>

          <FormInput
            label="Pronouns"
            error={!!errors.pronouns}
            disabled={disabled}
            {...register('pronouns')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Primary email address"
            placeholder="Enter work email address"
            autoComplete="work email"
            error={!!errors.email}
            {...register('email')}
          />

          <FormInput
            label="Primary mobile number"
            placeholder="Enter work phone number"
            autoComplete="mobile tel"
            {...register('mobile_number')}
          />
        </div>

        {/* profile note */}
        <div className="flex flex-col space-y-2">
          <label
            htmlFor="note"
            className="text-sm font-medium text-gray-900 leading-5"
          >
            Profile note
          </label>
          <textarea
            id="note"
            className="shadow-sm border border-gray-300 rounded-md text-base leading-6 disabled:bg-gray-200 focus:ring-brand-500 focus:border-brand-500"
            rows={3}
            placeholder="Write a few sentences about yourself."
            disabled={disabled}
            {...register('note')}
          />
        </div>
      </div>
    </InfoPanelContainer>
  );
}
